import { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import {
  Button,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  mergeClasses,
} from '@fluentui/react-components';
import { FormActions, useFormLayoutStyles, useIsMobile } from '@moko/ui';
import {
  createPricingParameter,
  updatePricingParameter,
  type IPricingParameter,
  type IPricingParameterDefinition,
  type PricingParameterScope,
} from '../../services/pricingParameterService';
import { dateOnlyToPickerDate, toApiDateOnly } from '../../utils/dateOnly';
import {
  formatParameterValue,
  getParameterErrorMessage,
  initialFormValues,
  parameterLabel,
  parseParameterValue,
  toParameterBody,
  validateEffectiveFrom,
  validateEffectiveTo,
  validateParameterValue,
  type ParameterFormValues,
} from './pricingParameterHelpers';

export interface PricingParameterFormProps {
  scope: PricingParameterScope;
  pricingItemId: number;
  definitions: IPricingParameterDefinition[];
  record?: IPricingParameter;
  onClose: () => void;
  onSaved: (message: string) => void;
}

const PricingParameterForm = ({
  scope,
  pricingItemId,
  definitions,
  record,
  onClose,
  onSaved,
}: PricingParameterFormProps) => {
  const styles = useFormLayoutStyles();
  const isMobile = useIsMobile();
  const isEdit = record !== undefined;
  const [saveError, setSaveError] = useState<string | null>(null);
  const saveAbort = useRef<AbortController | null>(null);
  const form = useForm<ParameterFormValues>({
    defaultValues: initialFormValues(record),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { control, handleSubmit, getValues, trigger, formState } = form;
  const { errors, isSubmitting, isSubmitted } = formState;
  const key = useWatch({ control, name: 'key' });
  const definition = definitions.find((candidate) => candidate.key === key);

  useEffect(() => {
    const controller = new AbortController();
    saveAbort.current = controller;
    return () => controller.abort();
  }, []);

  const onSubmit = handleSubmit(async (values) => {
    setSaveError(null);
    const selected = definitions.find((item) => item.key === values.key);
    const parsed = selected
      ? parseParameterValue(selected, values.value)
      : undefined;
    if (!selected || !parsed?.ok) return;
    const abortController = saveAbort.current ?? new AbortController();
    const body = toParameterBody(pricingItemId, values, parsed.value);
    try {
      if (record) {
        await updatePricingParameter(abortController, scope, record.id, body);
      } else {
        await createPricingParameter(abortController, scope, body);
      }
      if (abortController.signal.aborted) return;
      onSaved(
        `Pricing parameter ${parameterLabel(values.key)} ${
          isEdit ? 'updated' : 'created'
        }.`
      );
    } catch (error) {
      if (abortController.signal.aborted) return;
      setSaveError(
        getParameterErrorMessage(error, 'Failed to save pricing parameter.')
      );
    }
  });

  const disabled = isSubmitting;

  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      {saveError && (
        <MessageBar intent="error">
          <MessageBarBody>{saveError}</MessageBarBody>
        </MessageBar>
      )}
      <Controller
        control={control}
        name="key"
        rules={{
          validate: (value) =>
            definitions.some((item) => item.key === value) ||
            'Parameter is required.',
        }}
        render={({ field }) => (
          <Field
            label="Parameter"
            required
            validationState={errors.key ? 'error' : 'none'}
            validationMessage={errors.key?.message}
          >
            <Dropdown
              value={field.value ? parameterLabel(field.value) : ''}
              selectedOptions={field.value ? [field.value] : []}
              placeholder="Select parameter"
              disabled={disabled}
              onOptionSelect={(_, data) =>
                field.onChange(data.optionValue ?? '')
              }
            >
              {definitions.map((item) => (
                <Option
                  key={item.key}
                  value={item.key}
                  text={parameterLabel(item.key)}
                >
                  {parameterLabel(item.key)}
                </Option>
              ))}
            </Dropdown>
          </Field>
        )}
      />
      <Controller
        control={control}
        name="value"
        rules={{
          validate: (value) =>
            definition
              ? validateParameterValue(definition)(value)
              : 'Select a parameter first.',
        }}
        render={({ field }) => (
          <Field
            label={definition ? `Value (${definition.unit}s)` : 'Value'}
            required
            hint={
              definition
                ? `${formatParameterValue(
                    definition,
                    definition.minimum
                  )} to ${formatParameterValue(
                    definition,
                    definition.maximum
                  )}. Zero is a valid value; blank is not.`
                : undefined
            }
            validationState={errors.value ? 'error' : 'none'}
            validationMessage={errors.value?.message}
          >
            <Input
              inputMode="decimal"
              value={field.value}
              disabled={disabled}
              onChange={(_, data) => field.onChange(data.value)}
              onBlur={field.onBlur}
            />
          </Field>
        )}
      />
      <div
        className={mergeClasses(
          styles.twoCol,
          isMobile && styles.twoColStacked
        )}
      >
        <div className={styles.col}>
          <Controller
            control={control}
            name="effectiveFrom"
            rules={{ validate: validateEffectiveFrom }}
            render={({ field }) => (
              <Field
                label="Effective from"
                required
                validationState={errors.effectiveFrom ? 'error' : 'none'}
                validationMessage={errors.effectiveFrom?.message}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  value={dateOnlyToPickerDate(field.value)}
                  disabled={disabled}
                  onSelectDate={(selected) => {
                    field.onChange(toApiDateOnly(selected));
                    if (isSubmitted) void trigger('effectiveTo');
                  }}
                />
              </Field>
            )}
          />
        </div>
        <div className={styles.col}>
          <Controller
            control={control}
            name="effectiveTo"
            rules={{
              validate: (value) =>
                validateEffectiveTo(value, getValues('effectiveFrom')),
            }}
            render={({ field }) => (
              <Field
                label="Effective to"
                hint="Leave empty for no end date."
                validationState={errors.effectiveTo ? 'error' : 'none'}
                validationMessage={errors.effectiveTo?.message}
              >
                <div style={{ display: 'flex', gap: 4 }}>
                  <DatePicker
                    style={{ flex: 1, minWidth: 0 }}
                    value={dateOnlyToPickerDate(field.value)}
                    disabled={disabled}
                    onSelectDate={(selected) =>
                      field.onChange(toApiDateOnly(selected))
                    }
                  />
                  <Button
                    appearance="subtle"
                    disabled={disabled || !field.value}
                    onClick={() => field.onChange('')}
                  >
                    Clear
                  </Button>
                </div>
              </Field>
            )}
          />
        </div>
      </div>
      <FormActions
        disabled={disabled}
        submitting={isSubmitting}
        submitLabel={isEdit ? 'Save' : 'Create'}
        onClose={onClose}
      />
    </form>
  );
};

export default PricingParameterForm;
